import React, { useState, useRef, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  WHITE,
  TEXT_PRIMARY,
  COLOR_SOLID,
  AI_PRIMARY,
  AI_PRIMARY_HOVER,
  AI_ACCENT,
  AI_ACCENT_LIGHT,
  SEARCH_BORDER,
  SEARCH_SURFACE_HOVER,
  SEARCH_DIVIDER,
} from "@/constants/colors";
import { NARROW_SCREEN } from "./TransactionSearchPage.styled";

const PillRoot = styled.div`
  position: relative;
  display: inline-block;
`;

/**
 * PILL_SIZES
 *
 * Every legal pill size, enumerated here rather than overridden by a host
 * (WCHK-3083 review). `default` is the filters group. `large` is the search
 * row's date filter, which lines up with the search bar and the filters
 * toggle at 52px and takes the whole column when the row stacks.
 */
const PILL_SIZES = {
  default: { height: "36px", radius: "4px", fillsNarrowScreen: false },
  large: { height: "52px", radius: "4px", fillsNarrowScreen: true },
};

export const PILL_SIZE_NAMES = Object.keys(PILL_SIZES);

const pillSize = ($size) => PILL_SIZES[$size] ?? PILL_SIZES.default;

const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: ${({ $size }) => pillSize($size).height};
  padding: 0 12px;
  border-radius: ${({ $size }) => pillSize($size).radius};
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $isActive, $isOpen }) =>
    $isActive ? AI_PRIMARY : $isOpen ? SEARCH_SURFACE_HOVER : WHITE};
  color: ${({ $isActive }) => ($isActive ? WHITE : COLOR_SOLID)};
  border: 1px solid ${({ $isActive }) => ($isActive ? AI_PRIMARY : SEARCH_BORDER)};
  /* A disabled pill keeps its colors: the host dims the whole row. */
  &:disabled {
    cursor: default;
  }
  &:hover:not(:disabled) {
    background: ${({ $isActive }) =>
      $isActive ? AI_PRIMARY_HOVER : SEARCH_SURFACE_HOVER};
  }
  &:focus {
    outline: 2px solid ${AI_ACCENT};
    outline-offset: 2px;
  }
  ${NARROW_SCREEN} {
    /* Stacked, the large pill spans the column like the search field above
       it, and its label and value sit at the two ends. */
    ${({ $size }) =>
      pillSize($size).fillsNarrowScreen &&
      `
        width: 100%;
        justify-content: space-between;
      `}
  }
`;

/**
 * PillText
 *
 * Holds the face text at the widest text's width for hosts that pass a
 * `widthReservation`. Proportional figures make one `MM/DD/YYYY` range up to
 * 13px wider than another, and the difference landed in the search field
 * beside it as a jump (WCHK-3037). The reserving pseudo-element keeps one
 * label in the accessibility tree.
 */
const PillText = styled.span`
  display: inline-grid;
  justify-items: start;
  & > span,
  &::after {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
  &::after {
    content: var(--pill-width-reservation, "");
    visibility: hidden;
    pointer-events: none;
  }
`;

const Chevron = styled.svg`
  flex-shrink: 0;
  transition: transform 0.15s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 320px;
  overflow-y: auto;
  background: ${WHITE};
  border: 1px solid ${SEARCH_BORDER};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 4px 0;
`;

const Option = styled.button`
  display: block;
  width: 100%;
  padding: 8px 12px;
  background: ${({ $isSelected }) =>
    $isSelected ? AI_ACCENT_LIGHT : "transparent"};
  color: ${({ $isSelected }) => ($isSelected ? AI_PRIMARY : COLOR_SOLID)};
  border: none;
  text-align: left;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${SEARCH_SURFACE_HOVER};
  }
  &:focus {
    outline: none;
    background: ${SEARCH_SURFACE_HOVER};
  }
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${COLOR_SOLID};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${SEARCH_SURFACE_HOVER};
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  margin: 0;
  cursor: pointer;
  accent-color: ${AI_PRIMARY};
`;

const MultiSelectFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${SEARCH_DIVIDER};
  background: ${WHITE};
  position: sticky;
  bottom: 0;
`;

export const ChipForm = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 260px;
`;

export const ChipInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${TEXT_PRIMARY};
  border: 1px solid ${SEARCH_BORDER};
  border-radius: 4px;
  outline: none;
  &:focus {
    border-color: ${AI_ACCENT};
    box-shadow: 0 0 0 1px ${AI_ACCENT};
  }
`;

export const ChipFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const ChipFormButton = styled.button`
  padding: 6px 12px;
  font-size: 0.8125rem;
  font-family: inherit;
  border-radius: 4px;
  cursor: pointer;
  background: ${({ $variant }) => ($variant === "primary" ? AI_PRIMARY : WHITE)};
  color: ${({ $variant }) => ($variant === "primary" ? WHITE : COLOR_SOLID)};
  border: 1px solid
    ${({ $variant }) => ($variant === "primary" ? AI_PRIMARY : SEARCH_BORDER)};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === "primary" ? AI_PRIMARY_HOVER : SEARCH_SURFACE_HOVER};
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const Chip = ({
  label,
  displayValue,
  isOpen,
  onToggle,
  onClose,
  dataQa,
  ariaHasPopup = "dialog",
  icon: Icon,
  // One of PILL_SIZE_NAMES. Hosts pick a size by name; they do not restyle
  // the pill from outside (WCHK-3083 review).
  size = "default",
  // The chevron promises a panel attached to the pill. A host whose popup is
  // a portaled dialog rather than that panel passes false; `aria-haspopup`
  // still holds, because the popup is the dialog (WCHK-3083 review).
  chevron = true,
  // Lets a host focus the pill button — for example to return focus when a
  // portaled dialog it opened closes. The Chip keeps its own ref for Escape
  // either way.
  buttonRef = null,
  // Whether the pill owns an attached panel whose expanded state it tracks.
  // The date pill's popup is a portaled modal it never gets open-state for
  // (it passes a constant `isOpen={false}`), so it opts out here — otherwise
  // the button would carry a permanent, and wrong, aria-expanded="false".
  hasPanel = true,
  // When true the panel only closes via the pill toggle or its own controls not outside clicks. The date pill sets this because its calendar renders in a portaled modal outside this pill's DOM, so a normal click-away check would treat interacting with the calendar as "outside" and collapse the panel. The same portal problem applies to the focus-out check below, so this flag gates both.
  disableClickAway = false,
  // Applies the open form's pending value. The Chip binds it to Enter anywhere
  // in the panel (WCAG 2.1.1: everything the Apply button does must be
  // reachable by keyboard). Callers whose options apply themselves on
  // activation (the single-select list) leave it unset.
  onApply = null,
  // The widest value this pill can show, so its width stops tracking the value
  // it holds (WCHK-3037). Unset for pills in the wrapping filters panel.
  widthReservation = null,
  // Freezes the pill while the value it shows is being replaced.
  disabled = false,
  // Overrides the populated style; unset, it follows displayValue.
  populated = null,
  children,
}) => {
  const rootRef = useRef(null);
  const pillButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen || disableClickAway) return undefined;
    const handleClickAway = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [isOpen, onClose, disableClickAway]);

  // The keyboard's click-away: when focus leaves the pill (Tab past the last
  // control, Shift+Tab out the front, or a click that lands on something
  // focusable elsewhere), the form closes and its pending edits are discarded,
  // exactly like the mousedown listener above. A null relatedTarget means the
  // window itself lost focus, not that the user moved on, so it never closes.
  const handleFocusOut = (event) => {
    if (!isOpen || disableClickAway) return;
    const next = event.relatedTarget;
    if (next && rootRef.current && !rootRef.current.contains(next)) {
      onClose();
    }
  };

  const handlePanelKeyDown = (event) => {
    // Escape closes without applying and hands focus back to the pill so it
    // is not dropped on <body> when the panel unmounts (WCAG 2.4.3).
    if (event.key === "Escape") {
      onClose();
      pillButtonRef.current?.focus();
      return;
    }
    if (event.key !== "Enter" || !onApply || event.defaultPrevented) return;
    // A focused button (Apply, Clear, a list option) already activates on
    // Enter by itself; applying here too would run two actions off one press.
    if (event.target.closest("button")) return;
    event.preventDefault();
    onApply();
  };

  // The populated style follows `populated` when set, else whether there is a
  // value. Callers pass `displayValue` as null for unmapped/empty selections so
  // those never surface on the pill face.
  const hasDisplayValue = displayValue != null && displayValue !== "";
  const isActive = populated ?? hasDisplayValue;
  const text = hasDisplayValue ? `${label}: ${displayValue}` : label;

  return (
    <PillRoot
      ref={rootRef}
      data-qa={dataQa}
      data-testid={dataQa}
      onBlur={handleFocusOut}
    >
      <PillButton
        ref={(node) => {
          pillButtonRef.current = node;
          if (buttonRef) buttonRef.current = node;
        }}
        type="button"
        $size={size}
        $isActive={isActive}
        $isOpen={isOpen}
        onClick={onToggle}
        disabled={disabled}
        aria-haspopup={ariaHasPopup}
        aria-expanded={hasPanel ? isOpen : undefined}
      >
        {Icon && (
          <IconSlot>
            <Icon />
          </IconSlot>
        )}
        <PillText
          style={
            widthReservation
              ? // A CSS string token, so it is quoted rather than interpolated.
                {
                  "--pill-width-reservation": JSON.stringify(
                    `${label}: ${widthReservation}`
                  ),
                }
              : undefined
          }
        >
          <span>{text}</span>
        </PillText>
        {chevron && (
          <Chevron
            $isOpen={isOpen}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Chevron>
        )}
      </PillButton>
      {isOpen && !disabled && (
        <Panel onKeyDown={handlePanelKeyDown}>{children}</Panel>
      )}
    </PillRoot>
  );
};

/**
 * splitMultiValue
 *
 * Centralizing the split here keeps the FilterPill and the chip-display
 * callers using the same representation as the search epic, which splits the
 * same rawValue into the array form `qs.stringify` renders as `param[]=v1&param
 * []=v2`.
 *
 * @param {*} value - {String} comma-separated; ("" / "v1" / "v1,v2")
 * @returns {Array}
 */
const splitMultiValue = (value) =>
  typeof value === "string" && value.length > 0
    ? value.split(",").filter(Boolean)
    : [];

const FilterPill = ({
  label,
  value,
  options,
  onSelect,
  dataQa,
  icon,
  multiSelect = false,
  size = "default",
  // Set while a search is replacing the value this pill shows.
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");
  const isDropdownMode = Array.isArray(options);
  const isMultiSelect = multiSelect && isDropdownMode;

  // Pending selections live locally until Apply so click-away discards them, matching the free-text branch's "type then Apply" model.
  const [pendingSelection, setPendingSelection] = useState(() =>
    splitMultiValue(value)
  );

  // Applying pending edits against a value a search is about to replace would
  // race that write (WCHK-3037).
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      setInputValue(value ?? "");
      if (isMultiSelect) setPendingSelection(splitMultiValue(value));
    }
    setIsOpen((open) => !open);
  };

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSelectOption = (optionValue) => {
    onSelect(optionValue);
    setIsOpen(false);
  };

  const handleApply = () => {
    onSelect(inputValue.trim());
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    setPendingSelection([]);
    onSelect("");
    setIsOpen(false);
  };

  const handleMultiToggle = (optionValue) => {
    // "All ..." option (value === "") clears selections to "no filter" state.
    if (optionValue === "") {
      setPendingSelection([]);
      return;
    }
    setPendingSelection((prev) =>
      prev.includes(optionValue)
        ? prev.filter((v) => v !== optionValue)
        : [...prev, optionValue]
    );
  };

  const handleMultiApply = () => {
    onSelect(pendingSelection.join(","));
    setIsOpen(false);
  };

  const isActive = value !== "" && value != null;
  // displayValue is what the pill shows when a value is set
  let displayValue = null;
  if (isActive) {
    if (isMultiSelect) {
      const mappedValues = splitMultiValue(value)
        .map((v) => options.find((o) => o.value === v)?.text)
        .filter(Boolean);
      displayValue = mappedValues.length > 0 ? mappedValues.join(", ") : null;
    } else if (isDropdownMode) {
      // Dropdown filters resolve to the matching option's display text and deliberately fall back to null (not the raw value) so an unmapped/translated value never lands on the pill face
      displayValue = options.find((opt) => opt.value === value)?.text ?? null;
    } else {
      // Free-text criteria (account number, amount, …) show the literal value.
      displayValue = value;
    }
  }

  return (
    <Chip
      label={label}
      displayValue={displayValue}
      isOpen={isOpen}
      onToggle={handleToggle}
      onClose={handleClose}
      dataQa={dataQa}
      ariaHasPopup={isDropdownMode ? "listbox" : "dialog"}
      icon={icon}
      size={size}
      disabled={disabled}
      // The single-select list applies on option activation, so Enter already
      // works there natively; the other two modes route Enter to their Apply.
      onApply={
        isMultiSelect ? handleMultiApply : isDropdownMode ? null : handleApply
      }
    >
      {isMultiSelect ? (
        <>
          {options.map((option) => {
            const isAllOption = option.value === "";
            const checked = isAllOption
              ? pendingSelection.length === 0
              : pendingSelection.includes(option.value);
            return (
              <CheckboxOption key={option.value || "__all__"}>
                <Checkbox
                  checked={checked}
                  onChange={() => handleMultiToggle(option.value)}
                />
                <span>{option.text}</span>
              </CheckboxOption>
            );
          })}
          <MultiSelectFooter>
            {(isActive || pendingSelection.length > 0) && (
              <ChipFormButton type="button" onClick={handleClear}>
                Clear
              </ChipFormButton>
            )}
            <ChipFormButton
              type="button"
              $variant="primary"
              onClick={handleMultiApply}
            >
              Apply
            </ChipFormButton>
          </MultiSelectFooter>
        </>
      ) : isDropdownMode ? (
        options.map((option) => (
          <Option
            key={option.value}
            type="button"
            role="option"
            aria-selected={option.value === value}
            $isSelected={option.value === value}
            onClick={() => handleSelectOption(option.value)}
          >
            {option.text}
          </Option>
        ))
      ) : (
        <ChipForm>
          {/* Enter and Escape are handled by the Chip's panel, which owns the
              keyboard behavior for every form mode. */}
          <ChipInput
            autoFocus
            value={inputValue}
            placeholder={`Enter ${label.toLowerCase()}`}
            onChange={(event) => setInputValue(event.target.value)}
          />
          <ChipFormActions>
            {isActive && (
              <ChipFormButton type="button" onClick={handleClear}>
                Clear
              </ChipFormButton>
            )}
            <ChipFormButton type="button" $variant="primary" onClick={handleApply}>
              Apply
            </ChipFormButton>
          </ChipFormActions>
        </ChipForm>
      )}
    </Chip>
  );
};

export default FilterPill;
